import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { config } from '@/core/config'
import { useAuthStore } from '@/store/auth.store'
import type {
  EmpDocument,
  EmpDocumentListItem,
  EmpDocumentListResponse,
  DocumentTypeOption,
} from '@/api/types'

export class EmpDocumentService {
  async getList(
    branchId: number,
    empId: number,
    pageNumber = 1,
    pageSize = 10
  ): Promise<EmpDocumentListResponse> {
    const url = endpoints.empDocument.getList(branchId, empId, pageNumber, pageSize)
    const res = await apiClient.get<EmpDocumentListResponse>(url)
    const raw = res.data
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) {
      return {
        data: (raw as any).data as EmpDocumentListItem[],
        totalRecords: Number((raw as any).totalRecords) ?? 0,
        pageNumber: (raw as any).pageNumber ?? pageNumber,
        pageSize: (raw as any).pageSize ?? pageSize,
      }
    }
    return { data: [], totalRecords: 0, pageNumber, pageSize }
  }

  async getById(id: number): Promise<EmpDocument> {
    const url = endpoints.empDocument.getById(id)
    const res = await apiClient.get<EmpDocument | { data: EmpDocument }>(url)
    const raw = res.data
    if (raw && typeof raw === 'object' && 'data' in raw) return (raw as any).data
    return raw as EmpDocument
  }

  async getByEmpId(empId: number): Promise<EmpDocumentListItem[]> {
    const url = endpoints.empDocument.getByEmpId(empId)
    const res = await apiClient.get<EmpDocumentListItem[] | { data: EmpDocumentListItem[] }>(url)
    const raw = res.data
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && 'data' in raw) return (raw as any).data ?? []
    return []
  }

  async getDocumentTypes(): Promise<DocumentTypeOption[]> {
    const url = endpoints.empDocument.getDocumentTypes
    const res = await apiClient.get<DocumentTypeOption[] | { data: DocumentTypeOption[] }>(url)
    const raw = res.data
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as any).data)) return (raw as any).data
    return []
  }

  async addDocument(formData: FormData): Promise<unknown> {
    const baseUrl = config.api.baseUrl?.replace(/\/$/, '') || ''
    const path = endpoints.empDocument.add
    const url = `${baseUrl}${path.startsWith('/') ? path : '/' + path}`
    const { token } = useAuthStore.getState()
    const headers: HeadersInit = { Accept: 'application/json' }
    if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, { method: 'POST', body: formData, headers })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw Object.assign(new Error(body?.message ?? body?.Message ?? 'Add document failed'), {
        response: { status: res.status, data: body },
      })
    }
    return body
  }

  async updateDocument(documentId: number, formData: FormData): Promise<unknown> {
    const baseUrl = config.api.baseUrl?.replace(/\/$/, '') || ''
    const path = endpoints.empDocument.update(documentId)
    const url = `${baseUrl}${path.startsWith('/') ? path : '/' + path}`
    const { token } = useAuthStore.getState()
    const headers: HeadersInit = { Accept: 'application/json' }
    if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, { method: 'PUT', body: formData, headers })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw Object.assign(new Error(body?.message ?? body?.Message ?? 'Update document failed'), {
        response: { status: res.status, data: body },
      })
    }
    return body
  }

  async deleteDocument(documentId: number): Promise<void> {
    const url = endpoints.empDocument.delete(documentId)
    await apiClient.delete(url)
  }
}

export const empDocumentService = new EmpDocumentService()
